/**
 * QL-661 — the ONE definition of a stored date (`YYYY-MM-DD`), shared by the
 * public-API edge schemas, the party/identity writers and conditional display,
 * so they can never disagree on what a valid date is.
 */

/** Shape only: `YYYY-MM-DD`. Use `isValidIsoDate` to also reject `2024-02-31`. */
export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True for a real calendar date in `YYYY-MM-DD`. The regex alone accepts an
 * impossible date (`2024-02-31`, `2024-13-01`); `Date.UTC` normalises those
 * (Feb 31 rolls into March), so comparing the parts back is what catches it.
 */
export function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_PATTERN.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/**
 * Year-first, unambiguous loose spellings: `19800101`, `1980/01/01`,
 * `1980.1.1`. Unpadded month/day only with a separator (`198011` is ambiguous).
 */
const LOOSE_YEAR_FIRST = /^(?:(\d{4})(\d{2})(\d{2})|(\d{4})([-/.])(\d{1,2})\5(\d{1,2}))$/;

/**
 * Normalise a date typed or received from outside (AML screening input, a
 * vendor payload) to `YYYY-MM-DD`. Accepts ISO and the unambiguous year-first
 * spellings Postgres used to coerce silently; anything else (including
 * day/month-first forms, which are ambiguous) and impossible dates → `null`.
 */
export function normalizeIsoDate(raw: string): string | null {
  const match = LOOSE_YEAR_FIRST.exec(raw.trim());
  if (!match) return null;
  const year = match[1] ?? match[4];
  const month = (match[2] ?? match[6] ?? "").padStart(2, "0");
  const day = (match[3] ?? match[7] ?? "").padStart(2, "0");
  const iso = `${year}-${month}-${day}`;
  return isValidIsoDate(iso) ? iso : null;
}
